import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export interface SessionLogRecord {
  [key: string]: JsonValue;
}

export type SessionLogDownloadResult =
  | { status: "downloaded"; count: number }
  | { status: "missing" }
  | { status: "empty" }
  | { status: "noMatches" };

export const isLocalhost = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".local")
  );
};

const isJsonObject = (
  value: JsonValue,
): value is { [key: string]: JsonValue } => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const parseSessionLogBuffer = (
  rawLogs: string | null,
): SessionLogRecord[] | null => {
  if (!rawLogs) {
    return null;
  }

  const parsed = JSON.parse(rawLogs) as JsonValue;
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter(isJsonObject);
};

export const filterSessionLogsByMessage = (
  logs: readonly SessionLogRecord[],
  filterTerm: string,
): SessionLogRecord[] => {
  const lowerFilterTerm = filterTerm.toLowerCase();
  return logs.filter((log) => {
    const message = log.message;
    return (
      typeof message === "string" &&
      message.toLowerCase().includes(lowerFilterTerm)
    );
  });
};

const downloadBlob = (payload: string, filename: string) => {
  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getSessionLogBuffer = () =>
  parseSessionLogBuffer(
    localStorage.getItem(FrontendLoggerFactory.getSessionLogStorageKey()),
  );

export const downloadSessionLogBuffer = async (
  filenamePrefix = "frontend-admin-logs",
): Promise<SessionLogDownloadResult> => {
  await FrontendLoggerFactory.flush();
  const parsed = getSessionLogBuffer();

  if (!parsed) {
    return { status: "missing" };
  }

  if (parsed.length === 0) {
    return { status: "empty" };
  }

  downloadBlob(
    JSON.stringify(parsed, null, 2),
    `${filenamePrefix}-${new Date().toISOString()}.json`,
  );

  return { status: "downloaded", count: parsed.length };
};

export const downloadFilteredSessionLogBuffer = async (
  filterTerm: string,
): Promise<SessionLogDownloadResult> => {
  await FrontendLoggerFactory.flush();
  const parsed = getSessionLogBuffer();

  if (!parsed) {
    return { status: "missing" };
  }

  const filtered = filterSessionLogsByMessage(parsed, filterTerm);

  if (filtered.length === 0) {
    return { status: "noMatches" };
  }

  downloadBlob(
    JSON.stringify(filtered, null, 2),
    `frontend-admin-logs-filtered-${new Date().toISOString()}.json`,
  );

  return { status: "downloaded", count: filtered.length };
};

export const clearSessionLogBuffer = () => {
  localStorage.removeItem(FrontendLoggerFactory.getSessionLogStorageKey());
  window.dispatchEvent(new Event("HC_CLEAR_LOGS"));
};
